import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { CheckCircle, Loader2, Package, Mail, Gift, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterOptIn = ({ orderId }) => {
  const { t, i18n } = useTranslation();
  const [agreed, setAgreed] = useState(true);
  const [status, setStatus] = useState('idle'); // idle | loading | done | already | error
  const [errorMsg, setErrorMsg] = useState('');

  if (!orderId) return null;

  const submit = async () => {
    if (!agreed) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          language: (i18n.language || 'sk').split('-')[0].toLowerCase(),
          source: 'checkout_success',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus('error');
        setErrorMsg(data.detail || t('checkout.newsletter_error', 'Skúste to znova neskôr'));
        return;
      }
      setStatus(data.status === 'already_confirmed' ? 'already' : 'done');
    } catch (_e) {
      setStatus('error');
      setErrorMsg(t('checkout.newsletter_error', 'Skúste to znova neskôr'));
    }
  };

  if (status === 'done') {
    return (
      <div className="mt-8 border border-orange-200 bg-orange-50 rounded-xl p-5 text-left" data-testid="checkout-newsletter-done">
        <div className="flex items-start gap-3">
          <Gift className="h-6 w-6 text-orange-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-orange-900">
              {t('checkout.newsletter_done_title', 'Skontrolujte svoj email')}
            </h3>
            <p className="text-sm text-orange-800 mt-1">
              {t('checkout.newsletter_done_desc', 'Poslali sme Vám potvrdzovací odkaz. Po kliknutí dostanete jednorazový kupón -5% (od 100€) na ďalší nákup.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'already') {
    return (
      <div className="mt-8 border border-green-200 bg-green-50 rounded-xl p-4 text-sm text-green-800" data-testid="checkout-newsletter-already">
        {t('checkout.newsletter_already', 'Už ste prihlásený, ďakujeme!')}
      </div>
    );
  }

  return (
    <div className="mt-8 border-2 border-dashed border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 text-left" data-testid="checkout-newsletter-optin">
      <div className="flex items-start gap-3 mb-3">
        <Gift className="h-7 w-7 text-orange-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-bold text-gray-900">
            {t('checkout.newsletter_offer_title', 'Získajte -5% na ďalší nákup')}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {t('checkout.newsletter_offer_desc', 'Prihláste sa k odberu noviniek a my Vám pošleme jednorazový kupón na zľavu 5% pri nákupe nad 100€. Platnosť 60 dní.')}
          </p>
        </div>
      </div>
      <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer mb-3">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5 h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
          data-testid="checkout-newsletter-checkbox"
        />
        <span>
          {t('checkout.newsletter_consent', 'Súhlasím so zasielaním marketingových emailov. Odhlásenie kedykoľvek jedným klikom.')}
        </span>
      </label>
      <Button
        onClick={submit}
        disabled={!agreed || status === 'loading'}
        className="bg-orange-500 hover:bg-orange-600 inline-flex items-center gap-2"
        data-testid="checkout-newsletter-submit"
      >
        {status === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
        {t('checkout.newsletter_offer_cta', 'Získať kupón')}
      </Button>
      {status === 'error' && (
        <p className="text-red-600 text-sm mt-2" data-testid="checkout-newsletter-error">{errorMsg}</p>
      )}
    </div>
  );
};

const CheckoutSuccess = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const paymentMethod = searchParams.get('method');
  const paypalPaymentId = searchParams.get('paymentId');
  const paypalPayerId = searchParams.get('PayerID');

  useEffect(() => {
    if (paymentMethod === 'bank_transfer' || paymentMethod === 'cash_on_delivery') {
      // Non-card payment - already successful
      setStatus('success');
      clearCart();
      return;
    }

    // Handle PayPal return
    if (paymentMethod === 'paypal' && paypalPaymentId && paypalPayerId) {
      const executePayPalPayment = async () => {
        try {
          const response = await fetch(
            `${API_URL}/api/payments/paypal/execute?payment_id=${paypalPaymentId}&payer_id=${paypalPayerId}`,
            { method: 'POST' }
          );
          
          if (response.ok) {
            setStatus('success');
            clearCart();
          } else {
            setStatus('error');
          }
        } catch (error) {
          console.error('PayPal execute error:', error);
          setStatus('error');
        }
      };
      
      executePayPalPayment();
      return;
    }

    if (!sessionId) {
      setStatus('error');
      return;
    }

    // Poll for Stripe payment status
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/payments/checkout/status/${sessionId}`);
        if (!response.ok) {
          throw new Error('Failed to check payment status');
        }

        const data = await response.json();
        setPaymentInfo(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          // GA4 purchase event
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'purchase', {
              transaction_id: data.order_id || sessionId,
              value: Number(data.amount_total ?? data.amount ?? 0),
              currency: (data.currency || 'EUR').toUpperCase(),
            });
          }
          clearCart();
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts >= maxAttempts - 1) {
          setStatus('error');
        } else {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, paymentMethod, clearCart]);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 text-orange-500 mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold mb-2">{t('checkout.checking_payment')}</h1>
              <p className="text-gray-600">{t('checkout.please_wait')}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2 text-green-600" data-testid="success-title">
                {t('checkout.order_success')}
              </h1>
              <p className="text-gray-600 mb-6">{t('checkout.order_success_message')}</p>
              
              {paymentMethod === 'bank_transfer' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                  <h3 className="font-semibold text-blue-800 mb-2">{t('checkout.bank_details')}</h3>
                  <p className="text-sm text-blue-700">
                    <strong>IBAN:</strong> SK00 0000 0000 0000 0000 0000<br />
                    <strong>BIC/SWIFT:</strong> TATRSKBX<br />
                    <strong>{t('checkout.order_number')}:</strong> {orderId}
                  </p>
                </div>
              )}

              {paymentMethod === 'cash_on_delivery' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
                  <h3 className="font-semibold text-yellow-800 mb-2">{t('checkout.cod_info')}</h3>
                  <p className="text-sm text-yellow-700">
                    {t('checkout.cod_message')}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-center gap-2 text-gray-500 mb-6">
                <Mail className="h-4 w-4" />
                <span>{t('checkout.confirmation_email')}</span>
              </div>

              <NewsletterOptIn orderId={orderId || paymentInfo?.order_id} />

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
                <Link to="/account">
                  <Button variant="outline" className="w-full sm:w-auto">
                    <Package className="h-4 w-4 mr-2" />
                    {t('checkout.view_orders')}
                  </Button>
                </Link>
                <Link to="/catalog">
                  <Button className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600">
                    {t('cart.continue_shopping')}
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="h-16 w-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">⏱️</span>
              </div>
              <h1 className="text-2xl font-bold mb-2 text-yellow-600">{t('checkout.session_expired')}</h1>
              <p className="text-gray-600 mb-6">{t('checkout.session_expired_message')}</p>
              <Link to="/cart">
                <Button className="bg-orange-500 hover:bg-orange-600">
                  {t('checkout.try_again')}
                </Button>
              </Link>
            </>
          )}

          {(status === 'error' || status === 'timeout') && (
            <>
              <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">❌</span>
              </div>
              <h1 className="text-2xl font-bold mb-2 text-red-600">{t('checkout.payment_error')}</h1>
              <p className="text-gray-600 mb-6">{t('checkout.payment_error_message')}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/cart">
                  <Button className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600">
                    {t('checkout.try_again')}
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="w-full sm:w-auto">
                    {t('nav.contact')}
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
